import { Fragment, useEffect, useState, useMemo } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Dropdown, Form, Modal, Row, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import PortalMenu from "../Common/PortalMenu";
import SignatureModal from "../Common/SignatureModal";
import PermissionWrapper from "../PermissionWrapper";

const DprList = () => {
 const navigate = useNavigate();
 const { id: projectId } = useParams();
 const user = useSelector((state) => state.auth?.userDetails || {});
 const currentRole = String(user?.role?.name || user?.roleName || user?.role || "").toLowerCase();
 const canAddDpr = !["client", "po"].includes(currentRole);
 const [dprs, setDprs] = useState([]);
 const [loading, setLoading] = useState(true);
const [searchParams] = useSearchParams();

 const [selectedDpr, setSelectedDpr] = useState(null);
 const [showModal, setShowModal] = useState(false);
 const [previewingPdf, setPreviewingPdf] = useState(false);
 const [downloadingCumulativePdf, setDownloadingCumulativePdf] = useState(false);
 const [showSignModal, setShowSignModal] = useState(false);
 const [approveId, setApproveId] = useState(null);
 const [commentText, setCommentText] = useState("");
 const [commentSubmitting, setCommentSubmitting] = useState(false);

  // Cumulative DPR Email Settings State
  const [showEmailSettingsModal, setShowEmailSettingsModal] = useState(false);
  const [emailSettings, setEmailSettings] = useState({
    enabled: true,
    toEmails: [],
    ccEmails: [],
    bccEmails: [],
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [newToEmailInput, setNewToEmailInput] = useState("");
  const [newCcEmailInput, setNewCcEmailInput] = useState("");
  const [newBccEmailInput, setNewBccEmailInput] = useState("");
  const [clientsList, setClientsList] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  const clientEmailOptions = useMemo(() => {
    const list = [];
    (clientsList || []).forEach((client) => {
      const clientName = client.name || "Client";
      if (client.gmail) {
        list.push({
          email: client.gmail.trim().toLowerCase(),
          label: `${clientName} (Main Client)`,
          group: "Clients (Main Email)",
        });
      }
      if (
        client.companyDetails?.companyEmail &&
        client.companyDetails.companyEmail.trim().toLowerCase() !== client.gmail?.trim().toLowerCase()
      ) {
        list.push({
          email: client.companyDetails.companyEmail.trim().toLowerCase(),
          label: `${clientName} (Company Email)`,
          group: "Clients (Main Email)",
        });
      }
      const contacts = Array.isArray(client.contactPersons)
        ? client.contactPersons
        : client.contactPerson
        ? [client.contactPerson]
        : [];
      contacts.forEach((cp) => {
        if (cp.email) {
          const cpName = cp.name || "Contact";
          const designation = cp.designation ? ` - ${cp.designation}` : "";
          list.push({
            email: cp.email.trim().toLowerCase(),
            label: `${cpName}${designation} (${clientName})`,
            group: "Contact Persons",
          });
        }
      });
    });
    return list;
  }, [clientsList]);

 const [workOrders, setWorkOrders] = useState([]);


 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 limit: 10,
 });

 
const initialWorkOrder = searchParams.get("workOrder") || "";

const [filters, setFilters] = usePersistentFilters("dprs_filters", {
 search: "",
 status: "",
 workOrder: initialWorkOrder, // KEY FIX
 fromDate: "",
 toDate: "",
});



useEffect(() => {
 fetchDprs();
}, [pagination.currentPage, pagination.limit, filters]);

useEffect(() => {
 fetchWorkOrders();
}, []);


 const fetchDprs = async () => {
 try {
 setLoading(true);

 const params = {
 page: pagination.currentPage,
 limit: pagination.limit,
 ...(filters.search && { search: filters.search }),
 ...(filters.status && { status: filters.status }),
 ...(filters.workOrder && { workOrder: filters.workOrder }),
 ...(projectId && { project: projectId }),
 ...(filters.fromDate && { fromDate: filters.fromDate }),
 ...(filters.toDate && { toDate: filters.toDate }),
 };

 const res = await api.get("/dpr", { params });

  if (res.data.success) {
  const validDprs = (res.data.data || []).filter(
    (dpr) => dpr.project && (dpr.project._id || dpr.project.projectName)
  );
  setDprs(validDprs);
  setPagination((prev) => ({
  ...prev,
  totalPages: res.data.pagination.totalPages,
  totalCount: res.data.pagination.totalCount,
  currentPage: res.data.pagination.currentPage,
  }));
  }
 } catch (error) {
 swal("Error", "Unable to load DPR data", "error");
 } finally {
 setLoading(false);
 }
 };

 const handlePageChange = (page) => {
 setPagination(prev => ({ ...prev, currentPage: page }));
 };

 const fetchWorkOrders = async () => {
 try {
 const res = await api.get("/work-orders?limit=1000");
 if (res.data.success) {
 setWorkOrders(res.data.data);
 }
 } catch {
 console.error("Failed to fetch work orders");
 }
};


 const sumManpower = (mp) => {
 if (!mp) return 0;

 return (
 mp.total ??
 (Number(mp.supervisors || 0) +
 Number(mp.engineers || 0) +
 Number(mp.skilled || 0) +
 Number(mp.unskilled || 0))
 );
};

 const getStatusBadge = (status) => {
 const map = {
 Draft: "secondary",
 Submitted: "warning",
 Approved: "success",
 Rejected: "danger",
 };
 return map[status] || "primary";
 };

 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(true);

 const res = await api.get(`/dpr/${id}/pdf`, {
 responseType: "blob",
 });

 const fileURL = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" })
 );

 window.open(fileURL, "_blank");
 } catch (err) {
 console.error("DPR PDF preview error:", err);
 swal("Error", "Failed to preview DPR PDF", "error");
 } finally {
 setPreviewingPdf(false);
 }
 };

 const handleDownloadPDF = async (id) => {
 try {
 const res = await api.get(`/dpr/${id}/pdf`, {
 responseType: "blob",
 });

 const url = window.URL.createObjectURL(new Blob([res.data]));
 const link = document.createElement("a");
 link.href = url;
 link.download = `DPR-${id}.pdf`;
 link.click();

 swal("Success!", "DPR PDF downloaded successfully", "success");
 } catch (err) {
 console.error("DPR PDF download error:", err);
 swal("Error", "Failed to download DPR PDF", "error");
 }
 };

  const handleDownloadCumulativePDF = async () => {
    if (downloadingCumulativePdf) return;
    try {
      setDownloadingCumulativePdf(true);
      const params = new URLSearchParams();

      if (projectId) params.set("project", projectId);
      if (filters.workOrder) params.set("workOrder", filters.workOrder);
      if (filters.fromDate) params.set("fromDate", filters.fromDate);
      if (filters.toDate) params.set("toDate", filters.toDate);
      if (filters.status) params.set("status", filters.status);
      if (filters.search) params.set("search", filters.search);

      const res = await api.get(`/dpr/cumulative-report/pdf?${params.toString()}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;

      const disposition = res.headers && (res.headers["content-disposition"] || res.headers.get?.("content-disposition"));
      let filename = "Cumulative-DPR-Report.pdf";
      if (disposition && disposition.indexOf("filename=") !== -1) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      swal("Success", "Cumulative DPR PDF downloaded successfully", "success");
    } catch (err) {
      console.error("Cumulative DPR PDF download error:", err);
      swal("Error", "Failed to download cumulative DPR PDF", "error");
    } finally {
      setDownloadingCumulativePdf(false);
    }
  };

  const fetchEmailSettings = async () => {
    setSettingsLoading(true);
    try {
      const activeProj =
        projectId ||
        searchParams.get("project") ||
        (filters?.project || "") ||
        (dprs.length > 0 ? (dprs[0]?.project?._id || dprs[0]?.project) : "");

      const res = await api.get("/dpr/cumulative-report/settings", {
        params: activeProj ? { project: activeProj } : {},
      });
      if (res.data.success) {
        setEmailSettings(res.data.data);
        if (Array.isArray(res.data.data?.clients)) {
          setClientsList(res.data.data.clients);
        }
      }
    } catch (err) {
      console.error("Fetch email settings error:", err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleOpenEmailSettings = () => {
    fetchEmailSettings();
    setNewToEmailInput("");
    setNewCcEmailInput("");
    setNewBccEmailInput("");
    setShowEmailSettingsModal(true);
  };

  const handleAddEmail = (field, inputVal, setInput) => {
    const val = (inputVal || "").trim().toLowerCase();
    if (!val) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) {
      swal("Invalid Email", "Please enter a valid email address", "warning");
      return;
    }
    const currentList = Array.isArray(emailSettings[field]) ? emailSettings[field] : [];
    if (currentList.includes(val)) {
      swal("Already Added", `This email is already in the ${field.replace("Emails", "").toUpperCase()} list`, "info");
      setInput("");
      return;
    }
    setEmailSettings((prev) => ({
      ...prev,
      [field]: [...(Array.isArray(prev[field]) ? prev[field] : []), val],
    }));
    setInput("");
  };

  const handleRemoveEmail = (field, emailToRemove) => {
    setEmailSettings((prev) => ({
      ...prev,
      [field]: (Array.isArray(prev[field]) ? prev[field] : []).filter((e) => e !== emailToRemove),
    }));
  };

  const handleSaveEmailSettings = async () => {
    setSavingSettings(true);
    try {
      const activeProj =
        projectId ||
        searchParams.get("project") ||
        (filters?.project || "") ||
        (dprs.length > 0 ? (dprs[0]?.project?._id || dprs[0]?.project) : "");

      const res = await api.patch("/dpr/cumulative-report/settings", {
        project: activeProj || undefined,
        enabled: emailSettings.enabled,
        toEmails: emailSettings.toEmails || [],
        ccEmails: emailSettings.ccEmails || [],
        bccEmails: emailSettings.bccEmails || [],
      });
      if (res.data.success) {
        setEmailSettings(res.data.data);
        swal("Success", "Cumulative DPR Email settings saved successfully", "success");
        setShowEmailSettingsModal(false);
      }
    } catch (err) {
      console.error("Save email settings error:", err);
      swal("Error", err.response?.data?.message || "Failed to save settings", "error");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDelete = async (id) => {
 const confirm = await swal({
 title: "Are you sure?",
 text: "This DPR will be permanently deleted",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 });

 if (confirm) {
 try {
 const res = await api.delete(`/dpr/${id}`);
 if (res.data.success) {
 swal("Deleted", "DPR has been removed", "success");
 fetchDprs();
 }
 } catch (error) {
 swal("Error", "Failed to delete DPR", "error");
 }
 }
 };

 const handleView = async (dpr) => {
 try {
 const res = await api.get(`/dpr/${dpr._id}`);
 if (res.data.success) {
 setSelectedDpr(res.data.data);
 setCommentText("");
 setShowModal(true);
 }
 } catch (err) {
 swal("Error", "Unable to load DPR details", "error");
 }
 };

 const handleAddComment = async () => {
 const text = commentText.trim();
 if (!text || !selectedDpr?._id) return;

 try {
 setCommentSubmitting(true);
 const res = await api.post(`/dpr/${selectedDpr._id}/comments`, {
 comment: text,
 });

 if (res.data.success) {
 const newComment = res.data.data;
 setSelectedDpr((prev) => ({
 ...prev,
 comments: [...(prev?.comments || []), newComment],
 }));
 setCommentText("");
 swal("Success", "Comment added successfully", "success");
 }
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Failed to add comment",
 "error",
 );
 } finally {
 setCommentSubmitting(false);
 }
 };

 const handleFilterChange = (name, value) => {
 setFilters((prev) => ({ ...prev, [name]: value }));
 setPagination((prev) => ({ ...prev, currentPage: 1 }));
 };

 /* ================= APPROVE / REJECT ================= */

 const handleApprove = async (id) => {
 const confirm = await swal({
 title: "Approve DPR?",
 text: "Signature is required for approval.",
 icon: "info",
 buttons: ["Cancel", "Provide Signature"],
 });

 if (!confirm) return;

 setApproveId(id);
 setShowSignModal(true);
 };

 const handleSaveSignature = async (file) => {
 try {
 setLoading(true);

 const formData = new FormData();
 formData.append("signature", file);

 const res = await api.patch(`/dpr/${approveId}/approve`, formData, {
 headers: {
 "Content-Type": "multipart/form-data",
 },
 });

 if (res.data.success) {
 swal("Approved!", "DPR approved successfully", "success");
 setShowSignModal(false);
 setApproveId(null);
 fetchDprs();
 }
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Failed to approve DPR",
 "error",
 );
 } finally {
 setLoading(false);
 }
 };

 const handleReject = async (id) => {
 const remarks = prompt("Enter rejection reason:");
 if (!remarks) return;

 try {
 const res = await api.patch(`/dpr/${id}/reject`, { remarks });
 if (res.data.success) {
 swal("Rejected!", "DPR rejected successfully", "success");
 fetchDprs();
 }
 } catch (err) {
 swal("Error", "Failed to reject", "error");
 }
 };

 // Fixed-position action menu (avoids table overflow clipping) 
 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24">
 <circle fill="#000000" cx="5" cy="12" r="2"></circle>
 <circle fill="#000000" cx="12" cy="12" r="2"></circle>
 <circle fill="#000000" cx="19" cy="12" r="2"></circle>
 </svg>
 );

 return (
 <Fragment>
 <PageTitle activeMenu="DPR" motherMenu="ITC" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>DPR List</Card.Title>

 <div className="d-flex gap-2 align-items-center flex-wrap">
 <Link
 to={
 filters.workOrder
 ? `/progress/report?workOrder=${filters.workOrder}`
 : "/progress/report"
 }
 className="btn btn-outline-primary btn-sm"
 >
 <i className="fas fa-chart-line me-1" /> Progress Report
 </Link>
 <Link
 to="/progress/plan"
 className="btn btn-outline-secondary btn-sm"
 >
 <i className="fas fa-upload me-1" /> Upload Plan
 </Link>
 {canAddDpr && (
 <PermissionWrapper user={user} required={["create:dprs"]}>
 <Link
 className="btn btn-primary"
 to={
 filters.workOrder
 ? `/dpr/add?workOrder=${filters.workOrder}`
 : "/dpr/add"
 }
 >
 <i className="fas fa-plus me-2" /> Add DPR
 </Link>
 </PermissionWrapper>
 )}
 </div>
 </Card.Header>

 <div className="d-flex flex-wrap align-items-end gap-2 px-4 py-3 border-bottom">
 <div className="d-flex flex-column" style={{ width: "180px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">
 Search DPR
 </label>
 <Form.Control
 size="sm"
 type="text"
 placeholder="Search DPR..."
 value={filters.search}
 onChange={(e) => handleFilterChange("search", e.target.value)}
 />
 </div>

 <div className="d-flex flex-column" style={{ width: "145px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">
 Status
 </label>
 <Form.Select
 size="sm"
 value={filters.status}
 onChange={(e) => handleFilterChange("status", e.target.value)}
 >
 <option value="">All Statuses</option>
 <option value="Draft">Draft</option>
 <option value="Submitted">Submitted</option>
 <option value="Approved">Approved</option>
 <option value="Rejected">Rejected</option>
 </Form.Select>
 </div>

 <div className="d-flex flex-column" style={{ width: "135px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">
 From Date
 </label>
 <Form.Control
 size="sm"
 type="date"
 value={filters.fromDate}
 onChange={(e) => handleFilterChange("fromDate", e.target.value)}
 />
 </div>

 <div className="d-flex flex-column" style={{ width: "135px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">
 To Date
 </label>
 <Form.Control
 size="sm"
 type="date"
 value={filters.toDate}
 onChange={(e) => handleFilterChange("toDate", e.target.value)}
 />
 </div>

 <div className="ms-auto d-flex flex-column" style={{ width: "65px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">
 Show
 </label>
 <Form.Select
 size="sm"
 value={pagination.limit}
 onChange={(e) =>
 setPagination((prev) => ({
 ...prev,
 currentPage: 1,
 limit: Number(e.target.value),
 }))
 }
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 </Form.Select>
 </div>

  <div className="md-3 d-flex align-items-end flex-wrap gap-2">
    {currentRole === "admin" && (
      <Button
        variant="outline-primary"
        size="md"
        onClick={handleOpenEmailSettings}
      >
        <i className="fas fa-envelope-open-text me-2" />
        Email Settings / CC
      </Button>
    )}

    <Button
      variant="primary"
      size="md"
      onClick={handleDownloadCumulativePDF}
      disabled={downloadingCumulativePdf}
    >
      {downloadingCumulativePdf ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          />
          Generating PDF...
        </>
      ) : (
        <>
          <i className="fas fa-file-pdf me-2" />
          Download Cumulative PDF
        </>
      )}
    </Button>
  </div>
 </div>
 <Card.Body>
 {loading ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary" />
 </div>
 ) : dprs.length === 0 ? (
 <div className="text-center py-5">
 <h5>No DPR Found</h5>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="text-start">
 <thead>
 <tr>
 <th className="text-start" style={{ width: "50px" }}>#</th>
 <th className="text-start" style={{ width: "140px" }}>DPR No</th>
 <th className="text-start" style={{ width: "150px" }}>Work Order</th>
 <th className="text-start" style={{ width: "150px" }}>Employee</th>
 <th className="text-start" style={{ width: "110px" }}>Date</th>
 <th className="text-start" style={{ width: "100px" }}>Manpower</th>
 <th className="text-start" style={{ width: "80px" }}>Issues</th>
 <th className="text-start" style={{ width: "100px" }}>Status</th>
 <th className="text-start" style={{ width: "80px" }}>Action</th>
 </tr>
 </thead>

 <tbody className="text-start">
 {dprs.map((dpr, i) => (
 <tr key={dpr._id}>
 <td className="text-start">
 {(pagination.currentPage - 1) * pagination.limit +
 i +
 1}
 </td>

 <td className="text-start">
 <strong>{dpr.dprNumber}</strong>
 </td>

 <td className="text-start">{dpr.workOrder?.woNo || "—"}</td>

 <td className="text-start">{dpr.submittedBy?.name || "N/A"}</td>

 <td className="text-start">{new Date(dpr.date).toLocaleDateString()}</td>

 <td className="text-start">{sumManpower(dpr.manpower)}</td>

 <td className="text-start">{dpr.issueDetails?.length > 0 ? "Yes" : "No"}</td>

 <td className="text-start">
 <Badge bg={getStatusBadge(dpr.status)}>
 {dpr.status}
 </Badge>
 </td>

 <td className="text-start">
 <Dropdown>
 <Dropdown.Toggle
 variant=""
 as="div"
 className="btn i-false p-0 cursor-pointer"
 >
 {svgMenu}
 </Dropdown.Toggle>
 <Dropdown.Menu align="end" as={PortalMenu}>
 <PermissionWrapper user={user} required={["read:dprs"]}>
 <Dropdown.Item onClick={() => handleView(dpr)}>
 <i className="fas fa-eye me-2" /> View
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["edit:dprs"]}>
 <Dropdown.Item onClick={() => handleView(dpr)}>
 <i className="fas fa-comment-dots me-2 text-info" /> Comment
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["edit:dprs"]}>
 <Dropdown.Item
 onClick={() =>
 navigate(
 filters.workOrder
 ? `/dpr/edit/${dpr._id}?workOrder=${filters.workOrder}`
 : `/dpr/edit/${dpr._id}`
 )
 }
 >
 <i className="fas fa-edit me-2" /> Edit
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["read:dprs"]}>
 <Dropdown.Item
 onClick={() => handlePreviewPDF(dpr._id)}
 disabled={previewingPdf}
 >
 <i className="fas fa-eye me-2 text-primary" /> Preview PDF
 </Dropdown.Item>
 <Dropdown.Item onClick={() => handleDownloadPDF(dpr._id)}>
 <i className="fas fa-file-pdf me-2 text-danger" /> Download PDF
 </Dropdown.Item>
 </PermissionWrapper>

 {(dpr.status === "Draft" || dpr.status === "Submitted") && (
 <>
 <Dropdown.Divider />
 <PermissionWrapper user={user} required={["approve:dprs"]}>
 <Dropdown.Item
 onClick={() => handleApprove(dpr._id)}
 className="text-success"
 >
 <i className="fas fa-check me-2" /> Approve
 </Dropdown.Item>
 </PermissionWrapper>
 <PermissionWrapper user={user} required={["reject:dprs"]}>
 <Dropdown.Item
 onClick={() => handleReject(dpr._id)}
 className="text-danger"
 >
 <i className="fas fa-times me-2" /> Reject
 </Dropdown.Item>
 </PermissionWrapper>
 </>
 )}

 <Dropdown.Divider />
 <PermissionWrapper user={user} required={["delete:dprs"]}>
 <Dropdown.Item
 className="text-danger"
 onClick={() => handleDelete(dpr._id)}
 >
 <i className="fas fa-trash me-2" /> Delete
 </Dropdown.Item>
 </PermissionWrapper>
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 {/* Pagination */}
 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={dprs.length}
 limit={pagination.limit}
 onPageChange={handlePageChange}
 />
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <SignatureModal
 show={showSignModal}
 onHide={() => {
 setShowSignModal(false);
 setApproveId(null);
 }}
 onSave={handleSaveSignature}
 title="Client Signature for DPR Approval"
 />

 {selectedDpr && (
 <Modal
 centered
 show={showModal}
 onHide={() => setShowModal(false)}
 size="lg"
 >
 <Modal.Header closeButton>
 <Modal.Title>DPR Details</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 <Row className="mb-3">
 <Col md={6}>
 <strong>DPR Number:</strong> {selectedDpr.dprNumber}
 </Col>
 <Col md={6}>
 <strong>Date:</strong>{" "}
 {new Date(selectedDpr.date).toLocaleDateString()}
 </Col>
 </Row>

 <Row className="mb-3">
 <Col md={6}>
 <strong>Work Order:</strong>{" "}
{selectedDpr.workOrder?.woNo || "—"} {selectedDpr.workOrder?.scope || ""}


 </Col>
 <Col md={6}>
 <strong>Status:</strong> {selectedDpr.status}
 </Col>
 </Row>

 <Row className="mb-3">
 <Col md={6}>
 <strong>Task:</strong>{" "}
 {selectedDpr.taskId?.title || "N/A"}
 </Col>
 <Col md={6}>
 <strong>Assigned To:</strong>{" "}
 {selectedDpr.employeeId?.name || "Unassigned"}
 </Col>
 </Row>

 {selectedDpr.taskId && (
 <Row className="mb-3">
 <Col md={4}>
 <strong>Estimated Hrs:</strong> {selectedDpr.taskId.estimatedHours}
 </Col>
 <Col md={4}>
 <strong>Deadline:</strong>{" "}
 {selectedDpr.taskId.plannedEndDate
 ? new Date(selectedDpr.taskId.plannedEndDate).toLocaleDateString()
 : "N/A"}
 </Col>
 <Col md={4}>
 <strong>Task Status:</strong> {selectedDpr.taskId.status}
 </Col>
 </Row>
 )}

 {selectedDpr.activitiesCompleted?.length > 0 && (
 <>
 <h5 className="mt-4">Activities Completed</h5>
 <Table bordered size="sm">
 <thead>
 <tr>
 <th>BOQ Item</th>
<th>Activity</th>
<th>BOQ Qty</th>
<th>Completed</th>
<th>Wastage</th>
<th>Status</th>
 </tr>
 </thead>

 <tbody>
 {selectedDpr.activitiesCompleted.map((a, i) => (
 <tr key={i}>
 <td>{a.boqName}</td>

 <td>{a.activity}</td>

 <td>
 {a.boqQty || "-"} {a.unit}
 </td>

 <td>
 {a.quantityCompleted} {a.unit}
 </td>

 <td className="text-danger fw-bold">
 {a.wastageQty || 0} {a.unit}
 </td>

 <td>{a.status}</td>
 </tr>
))}
 </tbody>
 </Table>
 </>
 )}

 {selectedDpr.issueDetails?.length > 0 && (
 <>
 <h5 className="mt-4">Issues</h5>
 <Table bordered size="sm">
 <thead>
 <tr>
 <th>Issue</th>
 <th>Severity</th>
 <th>Status</th>
 </tr>
 </thead>

 <tbody>
 {selectedDpr.issueDetails.map((issue, idx) => (
 <tr key={idx}>
 <td>{issue.issue}</td>
 <td>{issue.severity}</td>
 <td>{issue.status}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </>
 )}

 <h5 className="mt-4">Comments</h5>
 {selectedDpr.comments?.length > 0 ? (
 <div className="mb-3">
 {selectedDpr.comments.map((comment, idx) => {
 const commenter = comment.commentedBy?.name || comment.commentedBy?.email || comment.commentedBy || "User";
 const commentDate = comment.commentedAt
 ? new Date(comment.commentedAt).toLocaleString()
 : "";

 return (
 <div
 key={idx}
 className="border rounded p-2 mb-2 bg-light"
 >
 <div className="d-flex justify-content-between gap-3">
 <strong>{commenter}</strong>
 <small className="text-muted">{commentDate}</small>
 </div>
 <div className="mt-1">{comment.comment}</div>
 </div>
 );
 })}
 </div>
 ) : (
 <p className="text-muted mb-3">No comments added yet.</p>
 )}

 <Form.Group>
 <Form.Label className="fw-bold">Add Comment</Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 placeholder="Write a comment..."
 value={commentText}
 onChange={(e) => setCommentText(e.target.value)}
 />
 </Form.Group>
 </Modal.Body>

 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowModal(false)}>
 Close
 </Button>
 <Button
 variant="primary"
 onClick={handleAddComment}
 disabled={commentSubmitting || !commentText.trim()}
 >
 {commentSubmitting ? "Saving..." : "Add Comment"}
 </Button>
 </Modal.Footer>
 </Modal>
 )}

      {/* CUMULATIVE DPR EMAIL SETTINGS MODAL */}
      <Modal
        show={showEmailSettingsModal}
        onHide={() => setShowEmailSettingsModal(false)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton className="py-2 px-3">
          <div>
            <Modal.Title className="d-flex align-items-center gap-2 fs-5 mb-0">
              <i className="fas fa-envelope text-primary" />
              Cumulative DPR Email Settings
            </Modal.Title>
            {(emailSettings.projectName || (dprs.length > 0 && (dprs[0]?.project?.projectName || dprs[0]?.projectName))) && (
              <div className="small text-muted mt-1">
                Project: <strong className="text-dark">{emailSettings.projectName || (dprs[0]?.project?.projectName || dprs[0]?.projectName)}</strong>
              </div>
            )}
          </div>
        </Modal.Header>
        <Modal.Body className="p-3" style={{ maxHeight: "calc(80vh - 120px)", overflowY: "auto" }}>
          {settingsLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" />
              <p className="mt-2 text-muted">Loading settings...</p>
            </div>
          ) : (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded border">
                <div>
                  <strong className="d-block">Automated Fortnightly DPR Mail</strong>
                  <small className="text-muted">
                    Automatically sends report every 14 days to configured recipients for this project
                  </small>
                </div>
                <Form.Check
                  type="switch"
                  id="cumulative-email-switch"
                  checked={Boolean(emailSettings.enabled)}
                  onChange={(e) =>
                    setEmailSettings((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                    }))
                  }
                />
              </div>

              {/* TO RECIPIENTS */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold d-flex justify-content-between align-items-center mb-1">
                  <span>TO Recipients</span>
                  <span className="text-muted small fw-normal">Project client & contact persons</span>
                </Form.Label>
                <p className="text-muted small mb-2">
                  Only the recipients selected below will receive the Cumulative DPR report for this project.
                </p>
                {emailSettings.toEmails && emailSettings.toEmails.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2 mb-2 p-2 bg-light rounded border">
                    {emailSettings.toEmails.map((email, idx) => {
                      const match = clientEmailOptions.find((o) => o.email === email);
                      return (
                        <Badge
                          key={idx}
                          bg="primary"
                          className="d-flex align-items-center gap-2 px-2 py-2"
                          style={{ fontSize: "0.85rem", fontWeight: 400 }}
                        >
                          <span>
                            {match ? <strong className="me-1">{match.label}:</strong> : null}
                            {email}
                          </span>
                          <i
                            className="fas fa-times"
                            style={{ cursor: "pointer" }}
                            title="Remove"
                            onClick={() => handleRemoveEmail("toEmails", email)}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-muted small mb-2 p-2 bg-light rounded border">
                    No recipients selected yet. Choose clients/contacts from the dropdown below or enter custom email.
                  </div>
                )}

                {/* Dropdown for Clients & Contact Persons */}
                <div className="mb-2">
                  <Form.Select
                    size="sm"
                    disabled={loadingClients}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val) {
                        handleAddEmail("toEmails", val, () => {});
                        e.target.value = "";
                      }
                    }}
                    defaultValue=""
                  >
                    <option value="">
                      {loadingClients ? "Loading clients & contacts..." : "▾ Choose Client or Contact Person to Add..."}
                    </option>
                    {clientEmailOptions.filter((o) => o.group === "Clients (Main Email)").length > 0 && (
                      <optgroup label="🏢 Clients (Company / Main Email)">
                        {clientEmailOptions
                          .filter((o) => o.group === "Clients (Main Email)")
                          .map((item, idx) => (
                            <option key={`to-client-${idx}`} value={item.email}>
                              {item.label} — {item.email}
                            </option>
                          ))}
                      </optgroup>
                    )}
                    {clientEmailOptions.filter((o) => o.group === "Contact Persons").length > 0 && (
                      <optgroup label="👤 Contact Persons">
                        {clientEmailOptions
                          .filter((o) => o.group === "Contact Persons")
                          .map((item, idx) => (
                            <option key={`to-cp-${idx}`} value={item.email}>
                              {item.label} — {item.email}
                            </option>
                          ))}
                      </optgroup>
                    )}
                  </Form.Select>
                </div>

                {/* Manual Email Input */}
                <div className="d-flex gap-2">
                  <Form.Control
                    type="email"
                    size="sm"
                    placeholder="Or enter custom recipient email..."
                    value={newToEmailInput}
                    onChange={(e) => setNewToEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        handleAddEmail("toEmails", newToEmailInput, setNewToEmailInput);
                      }
                    }}
                  />
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleAddEmail("toEmails", newToEmailInput, setNewToEmailInput)}
                    disabled={!newToEmailInput.trim()}
                  >
                    Add
                  </Button>
                </div>
              </Form.Group>

              {/* CC RECIPIENTS */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold d-flex justify-content-between align-items-center mb-1">
                  <span>CC Recipients (Keep in Copy)</span>
                  <span className="text-muted small fw-normal">Optional</span>
                </Form.Label>
                <p className="text-muted small mb-2">
                  Emails added here will automatically be CC'd on every Fortnightly Cumulative DPR report email.
                </p>

                {emailSettings.ccEmails && emailSettings.ccEmails.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2 mb-2 p-2 bg-light rounded border">
                    {emailSettings.ccEmails.map((email, idx) => {
                      const match = clientEmailOptions.find((o) => o.email === email);
                      return (
                        <Badge
                          key={idx}
                          bg="info"
                          className="d-flex align-items-center gap-2 px-2 py-2 text-white"
                          style={{ fontSize: "0.85rem", fontWeight: 400 }}
                        >
                          <span>
                            {match ? <strong className="me-1">{match.label}:</strong> : null}
                            {email}
                          </span>
                          <i
                            className="fas fa-times"
                            style={{ cursor: "pointer" }}
                            title="Remove CC"
                            onClick={() => handleRemoveEmail("ccEmails", email)}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-muted small mb-2 p-2 bg-light rounded border">
                    No CC emails added yet. Choose from dropdown or enter below.
                  </div>
                )}

                {/* Dropdown for Clients & Contact Persons (CC) */}
                <div className="mb-2">
                  <Form.Select
                    size="sm"
                    disabled={loadingClients}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val) {
                        handleAddEmail("ccEmails", val, () => {});
                        e.target.value = "";
                      }
                    }}
                    defaultValue=""
                  >
                    <option value="">
                      {loadingClients ? "Loading clients & contacts..." : "▾ Choose Client or Contact Person for CC..."}
                    </option>
                    {clientEmailOptions.filter((o) => o.group === "Clients (Main Email)").length > 0 && (
                      <optgroup label="🏢 Clients (Company / Main Email)">
                        {clientEmailOptions
                          .filter((o) => o.group === "Clients (Main Email)")
                          .map((item, idx) => (
                            <option key={`cc-client-${idx}`} value={item.email}>
                              {item.label} — {item.email}
                            </option>
                          ))}
                      </optgroup>
                    )}
                    {clientEmailOptions.filter((o) => o.group === "Contact Persons").length > 0 && (
                      <optgroup label="👤 Contact Persons">
                        {clientEmailOptions
                          .filter((o) => o.group === "Contact Persons")
                          .map((item, idx) => (
                            <option key={`cc-cp-${idx}`} value={item.email}>
                              {item.label} — {item.email}
                            </option>
                          ))}
                      </optgroup>
                    )}
                  </Form.Select>
                </div>

                <div className="d-flex gap-2">
                  <Form.Control
                    type="email"
                    size="sm"
                    placeholder="Or enter custom CC email address (e.g. director@company.com)..."
                    value={newCcEmailInput}
                    onChange={(e) => setNewCcEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        handleAddEmail("ccEmails", newCcEmailInput, setNewCcEmailInput);
                      }
                    }}
                  />
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => handleAddEmail("ccEmails", newCcEmailInput, setNewCcEmailInput)}
                    disabled={!newCcEmailInput.trim()}
                  >
                    Add
                  </Button>
                </div>
              </Form.Group>

              {/* BCC RECIPIENTS */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  BCC Recipients (Blind Copy)
                </Form.Label>
                <p className="text-muted small mb-2">
                  Secret copy recipients for cumulative DPR reports.
                </p>

                {emailSettings.bccEmails && emailSettings.bccEmails.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mb-2 p-2 bg-light rounded border">
                    {emailSettings.bccEmails.map((email, idx) => (
                      <Badge
                        key={idx}
                        bg="secondary"
                        className="d-flex align-items-center gap-2 px-2 py-2"
                        style={{ fontSize: "0.85rem", fontWeight: 400 }}
                      >
                        <span>{email}</span>
                        <i
                          className="fas fa-times"
                          style={{ cursor: "pointer" }}
                          title="Remove BCC"
                          onClick={() => handleRemoveEmail("bccEmails", email)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="d-flex gap-2">
                  <Form.Control
                    type="email"
                    placeholder="Enter BCC email address..."
                    value={newBccEmailInput}
                    onChange={(e) => setNewBccEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        handleAddEmail("bccEmails", newBccEmailInput, setNewBccEmailInput);
                      }
                    }}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => handleAddEmail("bccEmails", newBccEmailInput, setNewBccEmailInput)}
                    disabled={!newBccEmailInput.trim()}
                  >
                    Add
                  </Button>
                </div>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="py-2 px-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowEmailSettingsModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSaveEmailSettings}
            disabled={savingSettings || settingsLoading}
          >
            {savingSettings ? "Saving..." : "Save Settings"}
          </Button>
        </Modal.Footer>
      </Modal>

 {previewingPdf && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{
 background: "rgba(0,0,0,0.4)",
 zIndex: 9999,
 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing DPR PDF…</h6>
 </div>
 </div>
 )}
 </Fragment>
 );
};

export default DprList;